// src/components/ui/NotificationModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Send, Loader2, AlertCircle } from 'lucide-react';

export default function NotificationModal({ isOpen, onClose, onSubmit, initialData = null }) {
  const [formData, setFormData] = useState({
    titre: '',
    message: '',
    type: 'information',
    statut: 'brouillon',
    date_debut_affichage: '',
    date_fin_affichage: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (initialData) {
      setFormData({
        id: initialData.id,
        titre: initialData.titre || '',
        message: initialData.message || '',
        type: initialData.type || 'information',
        statut: initialData.statut || 'brouillon',
        date_debut_affichage: initialData.date_debut_affichage ? initialData.date_debut_affichage.slice(0, 16) : '',
        date_fin_affichage: initialData.date_fin_affichage ? initialData.date_fin_affichage.slice(0, 16) : '',
      });
    } else {
      setFormData({
        titre: '',
        message: '',
        type: 'information',
        statut: 'brouillon', // Par défaut pour éviter une publication accidentelle
        date_debut_affichage: '',
        date_fin_affichage: '',
      });
    }
    setError(null);
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload = { ...formData };
      
      // Conversion des dates au format ISO si renseignées
      if (payload.date_debut_affichage) {
        payload.date_debut_affichage = new Date(payload.date_debut_affichage).toISOString();
      } else {
        delete payload.date_debut_affichage;
      }
      if (payload.date_fin_affichage) {
        payload.date_fin_affichage = new Date(payload.date_fin_affichage).toISOString();
      } else {
        delete payload.date_fin_affichage;
      }

      await onSubmit(payload);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-slate-50 border border-slate-200 rounded-3xl p-6 shadow-2xl space-y-6">
        <div className="flex items-center justify-between pb-4 border-b border-slate-200">
          <h3 className="text-lg font-bold text-white">
            {initialData ? 'Modifier la Notification' : 'Nouvelle Notification'}
          </h3>
          <button onClick={onClose} className="p-2 text-slate-500 hover:text-slate-900 rounded-2xl bg-white">
            <X size={18} />
          </button>
        </div>

        {error && (
          <div className="p-3 bg-rose-50 border border-rose-200 rounded-2xl text-rose-700 text-xs flex items-center gap-2">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-[11px] font-bold text-slate-600 uppercase">Titre *</label>
            <input
              type="text"
              required
              value={formData.titre}
              onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
              className="w-full mt-1 bg-white border border-slate-200 rounded-2xl p-3 text-sm text-white focus:outline-none focus:border-indigo-500"
              placeholder="Ex: Maintenance système"
            />
          </div>

          <div>
            <label className="text-[11px] font-bold text-slate-600 uppercase">Message *</label>
            <textarea
              required
              rows={3}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full mt-1 bg-white border border-slate-200 rounded-2xl p-3 text-sm text-white focus:outline-none focus:border-indigo-500"
              placeholder="Description détaillée de la notification..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] font-bold text-slate-600 uppercase">Type *</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full mt-1 bg-white border border-slate-200 rounded-2xl p-3 text-sm text-white focus:outline-none focus:border-indigo-500"
              >
                <option value="information">Information</option>
                <option value="maintenance">Maintenance</option>
                <option value="nouveaute">Nouveauté</option>
                <option value="alerte">Alerte</option>
              </select>
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-600 uppercase">Statut *</label>
              <select
                value={formData.statut}
                onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                className="w-full mt-1 bg-white border border-slate-200 rounded-2xl p-3 text-sm text-white focus:outline-none focus:border-indigo-500"
              >
                <option value="brouillon">Brouillon</option>
                <option value="active">Active</option>
                <option value="archivee">Archivée</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] font-bold text-slate-600 uppercase">Début d'affichage</label>
              <input
                type="datetime-local"
                value={formData.date_debut_affichage}
                onChange={(e) => setFormData({ ...formData, date_debut_affichage: e.target.value })}
                className="w-full mt-1 bg-white border border-slate-200 rounded-2xl p-2.5 text-xs text-white focus:outline-none focus:border-indigo-500"
              />
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-600 uppercase">Fin d'affichage</label>
              <input
                type="datetime-local"
                value={formData.date_fin_affichage}
                onChange={(e) => setFormData({ ...formData, date_fin_affichage: e.target.value })}
                className="w-full mt-1 bg-white border border-slate-200 rounded-2xl p-2.5 text-xs text-white focus:outline-none focus:border-indigo-500"
              />
            </div>
          </div>

          <div className="pt-4 flex justify-end gap-3 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 bg-white text-slate-600 rounded-2xl text-xs font-bold hover:bg-slate-100"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl text-xs font-bold flex items-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              <span>{initialData ? 'Mettre à jour' : 'Enregistrer'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}